/**
 * `ProblemMap` — карта разбора проблемы: тонкая обёртка с пропсами будущего
 * `<AgroMap>` (поток 6al.1, план §0.6). Пока `AgroMap` нет, внутри стоит
 * существующий `map/MapWidget`; замена — одна строка в теле `ProblemMap`.
 *
 * API:
 *   <ProblemMap
 *     layer="workStatus" | "water" | "quarantine" | "crop"
 *     data={{ workStatusById?, waterById?, quarantineById? }}
 *     filterIds={контуры хозяйства}  focusIds={что подогнать в кадр}
 *     height={420} caption="одна строка: что показано"
 *     farmName="имя хозяйства для карточки контура"
 *   />
 * Клик по контуру открывает боковую карточку (контур, площадь, хозяйство,
 * значение слоя, «Полная карточка контура») — со страницы не уводит.
 *
 * Откат слоя «Вода»: у нынешнего `MapWidget` слоя подачи нет. Тогда кадр
 * красит отчёт о поливе (`workStatusById`, если передан) и говорит это
 * подписью; когда 6al.1 даст слой `water`, заливка пойдёт из `waterById`.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { MapWidget } from '../../map/MapWidget';
import { useContourGeo } from '../../map/useContourGeo';
import type { ColorBy, WorkStatusValue } from '../../map/paint';
import type { ValuesLayer } from '../../map/layers';
import type { ContourMeta, QuarantineStatusValue } from '../../data/types';
import { DetailDrawer } from '../../ui/DetailDrawer';
import { EmptyState } from '../../ui/EmptyState';
import { haUnit, num } from '../../lib/format';
import { defineMessages, type PlainMessage } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import type { WaterState } from './model';
import './problems.css';

export type ProblemLayer = 'workStatus' | 'water' | 'waterEvents' | 'deadlines' | 'quarantine' | 'crop' | 'conflicts';

export interface ProblemMapData {
  workStatusById?: Record<string, WorkStatusValue>;
  waterById?: Record<string, WaterState>;
  quarantineById?: Record<string, QuarantineStatusValue>;
  /** Произвольный слой (`conflicts`: спорные контуры цветом проблемы, 6al.15). */
  values?: ValuesLayer;
}

export interface ProblemMapProps {
  layer: ProblemLayer;
  data?: ProblemMapData;
  filterIds?: string[];
  focusIds?: string[];
  /** Высота кадра; `'fill'` — тянуться на высоту родителя (flex-колонка), не ниже 420. */
  height?: number | 'fill';
  caption?: string;
  farmName?: string;
  /** Ссылочный хвост среза (`?as_of=…`) для «Полной карточки контура». */
  linkSearch?: string;
}

const m = defineMessages({
  workDone: { id: 'board.problemMap.work.done', defaultMessage: 'выполнено', description: 'Значение слоя карты в карточке контура: работа выполнена; строчными, после «Отчёт: »' },
  workInProgress: { id: 'board.problemMap.work.inProgress', defaultMessage: 'в процессе: отчёт есть, работа не закончена', description: 'Значение слоя карты в карточке контура: отчёт есть, но работа не закончена' },
  workInWindow: { id: 'board.problemMap.work.inWindow', defaultMessage: 'окно идёт, отчёта ещё нет', description: 'Значение слоя карты в карточке контура: окно открыто, отчёта нет' },
  workUpcoming: { id: 'board.problemMap.work.upcoming', defaultMessage: 'окно впереди', description: 'Значение слоя карты в карточке контура: окно работы ещё не началось' },
  workSilent: { id: 'board.problemMap.work.silent', defaultMessage: 'без отчёта после срока', description: 'Значение слоя карты в карточке контура: срок прошёл, отчёта нет' },
  waterDelivered: { id: 'board.problemMap.water.delivered', defaultMessage: 'подано', description: 'Значение слоя воды в карточке контура: вода подана; строчными, после «Подача: »' },
  waterPartial: { id: 'board.problemMap.water.partial', defaultMessage: 'подано частично', description: 'Значение слоя воды в карточке контура: подано меньше 95 % заявки' },
  waterNone: { id: 'board.problemMap.water.none', defaultMessage: 'запрос был, подачи по учёту нет', description: 'Значение слоя воды в карточке контура: заявка есть, подачи по учёту нет' },
  waterUnmeasured: { id: 'board.problemMap.water.unmeasured', defaultMessage: 'объём не измерен', description: 'Значение слоя воды в карточке контура: объём не измерен (пробел учёта, не ноль)' },
  quarantineRed: { id: 'board.problemMap.quarantine.red', defaultMessage: 'красный статус', description: 'Значение слоя карантина в карточке контура, после «Карантин: »' },
  quarantineYellow: { id: 'board.problemMap.quarantine.yellow', defaultMessage: 'жёлтый статус', description: 'Значение слоя карантина в карточке контура, после «Карантин: »' },
  quarantineGreen: { id: 'board.problemMap.quarantine.green', defaultMessage: 'зелёный', description: 'Значение слоя карантина в карточке контура, после «Карантин: »' },
  valueWater: { id: 'board.problemMap.value.water', defaultMessage: 'Подача: {state}', description: 'Строка в карточке контура: состояние подачи воды; {state} — слово из списка состояний' },
  valueIrrigation: { id: 'board.problemMap.value.irrigation', defaultMessage: 'Полив: {state}', description: 'Строка в карточке контура на карте «Вода» по файлу ирригации; {state} — подпись из легенды («полито — событие в файле ирригации»)' },
  valueWork: { id: 'board.problemMap.value.work', defaultMessage: 'Отчёт: {state}', description: 'Строка в карточке контура: состояние отчёта о работе; {state} — слово из списка состояний' },
  valueQuarantine: { id: 'board.problemMap.value.quarantine', defaultMessage: 'Карантин: {state}', description: 'Строка в карточке контура: карантинный статус; {state} — слово из списка статусов' },
  valueConflict: { id: 'board.problemMap.value.conflict', defaultMessage: 'Расхождение отчёта и проверки', description: 'Строка в карточке контура: есть расхождение отчёта и проверки' },
  valueNoConflict: { id: 'board.problemMap.value.noConflict', defaultMessage: 'Расхождения нет', description: 'Строка в карточке контура: расхождения нет' },
  noGeoTitle: { id: 'board.problemMap.noGeo.title', defaultMessage: 'Контуров хозяйства нет на карте', description: 'Заголовок пустого состояния карты разбора: у контуров хозяйства нет геометрии' },
  noGeoReason: { id: 'board.problemMap.noGeo.reason', defaultMessage: 'У {count, plural, one {# контура} few {# контуров} many {# контуров} other {# контуров}} хозяйства нет геометрии в выдаче карты. Это пробел данных, а не отсутствие земли; список в разборе от этого не меняется.', description: 'Пояснение пустого состояния карты разбора; # — число контуров хозяйства' },
  noContoursTitle: { id: 'board.problemMap.noContours.title', defaultMessage: 'Контуров для карты нет', description: 'Заголовок пустого состояния карты разбора: у хозяйства нет контуров' },
  noContoursReason: { id: 'board.problemMap.noContours.reason', defaultMessage: 'У хозяйства в этом срезе нет контуров с координатами или землепользование не загружено.', description: 'Пояснение пустого состояния карты разбора' },
  waterFallbackNote: { id: 'board.problemMap.waterFallbackNote', defaultMessage: 'Заливка — отчёт о поливе; подача по контурам появится со слоем воды карты.', description: 'Примечание под картой разбора «Вода»: слоя подачи пока нет, красится отчёт о поливе' },
  partlyDrawn: { id: 'board.problemMap.partlyDrawn', defaultMessage: 'На карте {drawn} из {total} контуров хозяйства: у остальных нет геометрии.', description: 'Примечание под картой разбора; {drawn}/{total} — числа контуров' },
  focusUndrawn: { id: 'board.problemMap.focusUndrawn', defaultMessage: '{missing, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} с сигналом из {total} без геометрии — на карте {missing, plural, one {его} other {их}} нет; список слева полный.', description: 'Примечание под картой разбора: часть контуров с сигналом (их подсвечивает карта) не имеет геометрии; {missing}/{total} — числа контуров' },
  contourTitle: { id: 'board.problemMap.contourTitle', defaultMessage: 'Контур №{no}', description: 'Заголовок выдвижной карточки контура на карте разбора; {no} — номер контура' },
  areaUnknown: { id: 'board.problemMap.areaUnknown', defaultMessage: 'Площадь не названа', description: 'Строка в карточке контура: источник не назвал площадь' },
  areaFarm: { id: 'board.problemMap.areaFarm', defaultMessage: '{area} · {farm}', description: 'Строка в карточке контура: площадь и имя хозяйства; {area} — «46,8 га» или «Площадь не названа»' },
  fullCard: { id: 'board.problemMap.fullCard', defaultMessage: 'Полная карточка контура', description: 'Кнопка в карточке контура на карте: открыть полную карточку; рядом стрелка' },
});

const WORK_LABEL: Record<WorkStatusValue, PlainMessage> = {
  reported_verified: m.workDone,
  reported: m.workDone,
  in_progress: m.workInProgress,
  in_window: m.workInWindow,
  upcoming: m.workUpcoming,
  silent: m.workSilent,
};
const WATER_LABEL: Record<WaterState, PlainMessage> = {
  delivered: m.waterDelivered,
  partial: m.waterPartial,
  none: m.waterNone,
  unmeasured: m.waterUnmeasured,
};
const Q_LABEL: Record<QuarantineStatusValue, PlainMessage> = { red: m.quarantineRed, watch: m.quarantineYellow, green: m.quarantineGreen };

/** Слои, которые умеет нынешний кадр. `water` появится с `<AgroMap>` (6al.1). */
const SUPPORTED: ReadonlySet<ProblemLayer> = new Set(['workStatus', 'quarantine', 'crop', 'conflicts', 'waterEvents', 'deadlines']);

/** Слой разбора → слой кадра: расхождения рисуются произвольным слоем значений. */
const COLOR_OF: Partial<Record<ProblemLayer, ColorBy>> = { conflicts: 'values', waterEvents: 'values', deadlines: 'values' };

export function ProblemMap({ layer, data, filterIds, focusIds, height = 420, caption, farmName, linkSearch = '' }: ProblemMapProps) {
  const t = useT();
  const [picked, setPicked] = useState<ContourMeta | null>(null);
  // Та же выдача геометрии, что у кадра (кэш на контекст): сколько контуров
  // хозяйства вообще есть на карте. Пустой кадр без слов читался бы как
  // «карта сломалась», а это пробел геометрии в данных.
  const geo = useContourGeo();
  const drawn = useMemo(() => {
    if (!filterIds || geo.state !== 'ready' || !geo.geo) return null;
    const have = new Set(geo.geo.features.map(f => String(f.properties?.id ?? '')));
    return filterIds.filter(id => have.has(id)).length;
  }, [filterIds, geo.state, geo.geo]);
  // Фокус — только по нарисованным контурам: контур с сигналом без геометрии
  // кадр не найдёт и молча откатится на весь район (так «пустела» карта
  // карантина у хозяйства, чьи сигнальные контуры без геометрии).
  const focus = useMemo(() => {
    const want = focusIds ?? filterIds;
    if (!want || geo.state !== 'ready' || !geo.geo) return { ids: want, missing: 0, total: 0 };
    const have = new Set(geo.geo.features.map(f => String(f.properties?.id ?? '')));
    const ids = want.filter(id => have.has(id));
    const missing = focusIds ? focusIds.length - ids.length : 0;
    return { ids: ids.length ? ids : filterIds?.filter(id => have.has(id)), missing, total: focusIds?.length ?? 0 };
  }, [focusIds, filterIds, geo.state, geo.geo]);
  if (filterIds && drawn === 0) {
    return <div className="pmap pmap--empty" style={{ minHeight: height === 'fill' ? 420 : height }}><EmptyState title={t(m.noGeoTitle)} reason={t(m.noGeoReason, { count: filterIds.length })} compact /></div>;
  }
  if (filterIds && !filterIds.length) {
    return <div className="pmap pmap--empty" style={{ minHeight: height === 'fill' ? 420 : height }}><EmptyState title={t(m.noContoursTitle)} reason={t(m.noContoursReason)} compact /></div>;
  }
  // Откат: слоя подачи у кадра нет — красим отчёт о поливе, если он есть.
  const effective: ColorBy = SUPPORTED.has(layer) ? (COLOR_OF[layer] ?? (layer as ColorBy)) : data?.workStatusById ? 'workStatus' : 'crop';
  const fallbackNote = layer === 'water' && !SUPPORTED.has('water') ? t(m.waterFallbackNote) : null;
  const valueOf = (id: string): string | null => {
    if (layer === 'water' && data?.waterById?.[id]) return t(m.valueWater, { state: t(WATER_LABEL[data.waterById[id]!]) });
    if (data?.workStatusById?.[id]) return t(m.valueWork, { state: t(WORK_LABEL[data.workStatusById[id]!]) });
    if (data?.quarantineById?.[id]) return t(m.valueQuarantine, { state: t(Q_LABEL[data.quarantineById[id]!]) });
    if (layer === 'conflicts') return t(data?.values?.byId[id] ? m.valueConflict : m.valueNoConflict);
    if (layer === 'deadlines' && data?.values) {
      const key = data.values.byId[id];
      const label = key ? data.values.legend.find(e => e.key === key)?.label : null;
      return t(m.valueWork, { state: label ?? data.values.noneLabel ?? '' });
    }
    if (layer === 'waterEvents' && data?.values) {
      const key = data.values.byId[id];
      const label = key ? data.values.legend.find(e => e.key === key)?.label : null;
      return t(m.valueIrrigation, { state: label ?? data.values.noneLabel ?? '' });
    }
    return null;
  };
  return (
    <div className={`pmap ${height === 'fill' ? 'pmap--fill' : ''}`}>
      <MapWidget
        colorBy={effective}
        filterIds={filterIds}
        focusIds={focus.ids}
        workStatusById={data?.workStatusById}
        quarantineById={data?.quarantineById}
        data={data?.values ? { values: data.values } : undefined}
        height={height === 'fill' ? undefined : height}
        fill={height === 'fill'}
        legend="below"
        caption={caption}
        onSelect={c => setPicked(c)}
      />
      {fallbackNote ? <p className="pmap__note">{fallbackNote}</p> : null}
      {focus.missing > 0 && focusIds !== filterIds ? <p className="pmap__note">{t(m.focusUndrawn, { missing: focus.missing, total: focus.total })}</p> : null}
      {filterIds && drawn !== null && drawn < filterIds.length ? <p className="pmap__note">{t(m.partlyDrawn, { drawn: num(drawn), total: num(filterIds.length) })}</p> : null}
      {picked ? (
        <DetailDrawer title={t(m.contourTitle, { no: picked.no })} onClose={() => setPicked(null)}>
          <div className="pmap-card">
            <p className="pmap-card__line">{farmName ? t(m.areaFarm, { area: picked.area_stated === false ? t(m.areaUnknown) : haUnit(picked.area_ha), farm: farmName }) : picked.area_stated === false ? t(m.areaUnknown) : haUnit(picked.area_ha)}</p>
            {valueOf(picked.id) ? <p className="pmap-card__value">{valueOf(picked.id)}</p> : null}
            <Link className="astra-button" to={`/contours/${picked.id}${layer === 'quarantine' ? `${linkSearch ? `${linkSearch}&` : '?'}tab=quarantine` : linkSearch}`}>{t(m.fullCard)} <ArrowUpRight size={15} /></Link>
          </div>
        </DetailDrawer>
      ) : null}
    </div>
  );
}
